import * as sdk from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import type firebase from "firebase/compat/app";
import type {} from "firebase/compat/firestore";
import { isUnknownArray, errorCode, isRecord } from "../types/data";
import { isFirestoreDocument, isFirestoreValue } from "../types/firestore";
import type { FirestoreValue, FirestoreDocument, FirestoreWrite, FieldTransform, FieldOperator,
  GatewayRequest, GatewayCursor, QueryFilter, QueryOrder, StructuredQuery } from "../types/firestore";

// Reference/query construction stays in the Firebase SDK. Only operations that
// access the database use the authenticated, metered application server.
export * from "firebase/firestore";

const PAGE_SIZE = 500;
const POLL_MS = 15000;
const REQUEST_TIMEOUT_MS = 60000;
const LOCAL_SERVER_PORT = process.env.REACT_APP_SHARE_SERVER_PORT || "3002";
interface SdkPath {
  canonicalString(): string;
  toArray?(): string[];
  segments: string[];
  offset: number;
  len: number;
}
interface SdkValue extends Omit<FirestoreValue, "timestampValue" | "bytesValue" | "arrayValue" | "mapValue"> {
  timestampValue?: string | { seconds: string | number; nanos?: number; nanoseconds?: number };
  bytesValue?: string | Uint8Array;
  arrayValue?: { values?: SdkValue[] };
  mapValue?: { fields?: Record<string, SdkValue> };
}
interface SdkFieldFilter { field: SdkPath; op: sdk.WhereFilterOp; value: SdkValue; filters?: undefined }
interface SdkCompositeFilter { filters: SdkFilter[]; op: "and" | "or" }
type SdkFilter = SdkFieldFilter | SdkCompositeFilter;
interface SdkQuery {
  path: SdkPath;
  collectionGroup: string | null;
  explicitOrderBy: { field: SdkPath; dir: "asc" | "desc" }[];
  filters: SdkFilter[];
  limit: number | null;
  limitType: "F" | "L";
  startAt: { position: SdkValue[]; inclusive: boolean } | null;
  endAt: { position: SdkValue[]; inclusive: boolean } | null;
}
interface SdkTransform { constructor: Function; elements?: SdkValue[]; operand?: SdkValue }
interface SdkMutationBase {
  key: { path: SdkPath };
  fieldTransforms: { field: SdkPath; transform: SdkTransform }[];
  precondition?: { exists?: boolean };
}
type SdkMutation = SdkMutationBase & (
  | { type: 0; value: { value: SdkValue } }
  | { type: 1; data: { value: SdkValue }; fieldMask: { fields: SdkPath[] } }
  | { type: 2 }
);
interface ParsedBatch extends Omit<sdk.WriteBatch, "set" | "update"> {
  _mutations: SdkMutation[];
  set<T>(reference: sdk.DocumentReference<T>, data: unknown, options?: sdk.SetOptions): ParsedBatch;
  update<T>(reference: sdk.DocumentReference<T>, ...args: unknown[]): ParsedBatch;
}
type InternalFirestore = sdk.Firestore & { _databaseId: { projectId: string; database: string } };
type InternalReference<T = sdk.DocumentData> = sdk.DocumentReference<T> & { _key: { path: SdkPath } };
type InternalQuery<T = sdk.DocumentData> = sdk.Query<T> & { _query: SdkQuery };
interface GatewayDocumentSnapshot<T = sdk.DocumentData> extends sdk.DocumentSnapshot<T> {
  _gatewayDocument: FirestoreDocument | null;
}
interface GatewayQueryDocumentSnapshot<T = sdk.DocumentData> extends sdk.QueryDocumentSnapshot<T> {
  _gatewayDocument: FirestoreDocument;
}
interface GatewayQuerySnapshot<T = sdk.DocumentData> extends sdk.QuerySnapshot<T> {
  docs: GatewayQueryDocumentSnapshot<T>[];
}
type Snapshot = GatewayDocumentSnapshot | GatewayQuerySnapshot;
type Observer = { next?: (snapshot: Snapshot) => void; error?: (error: unknown) => void; complete?: () => void };
const listeners = new Set<{ db: sdk.Firestore; refresh: () => void }>();
const compatDatabases = new WeakMap<firebase.firestore.Firestore, firebase.firestore.Firestore>();
const transformKinds = new WeakMap<sdk.Firestore, Map<Function, string>>();
const unwrap = <T,>(value: T | { _delegate: T }): T =>
  isRecord(value) && value._delegate ? value._delegate as T : value as T;
const databaseOf = (value: unknown): InternalFirestore => {
  const unwrapped = unwrap(value);
  const database = unwrap(isRecord(unwrapped) && unwrapped.firestore ? unwrapped.firestore : unwrapped);
  if (!isRecord(database) || !isRecord(database._databaseId) || typeof database._databaseId.projectId !== "string") {
    throw errorWithCode("Firestore database is unavailable");
  }
  return database as unknown as InternalFirestore;
};
const internalReference = <T,>(value: sdk.DocumentReference<T>): InternalReference<T> => unwrap(value) as InternalReference<T>;
const internalQuery = <T,>(value: sdk.Query<T>): InternalQuery<T> => unwrap(value) as InternalQuery<T>;
const metadata = () => ({
  hasPendingWrites: false,
  fromCache: false,
  isEqual(other: sdk.SnapshotMetadata) {
    return other?.hasPendingWrites === false && other?.fromCache === false;
  }
});

const errorWithCode = (message: string, code = "invalid-argument") =>
  Object.assign(new Error(message), { code });

const databaseName = (db: sdk.Firestore) => {
  const id = databaseOf(db)._databaseId;
  if (!id?.projectId) throw errorWithCode("Firestore database is unavailable");
  return `projects/${id.projectId}/databases/${id.database || "(default)"}/documents`;
};

const requestUrls = () => {
  const location = typeof window !== "undefined" ? window.location : null;
  const configured = String(process.env.REACT_APP_SHARE_SERVER_URL || "").replace(/\/$/, "");
  const loopback = ["localhost", "127.0.0.1", "::1"].includes(location?.hostname || "");
  const localReact = loopback && location?.port !== LOCAL_SERVER_PORT;
  const origin = configured || (localReact
    ? `http://localhost:${LOCAL_SERVER_PORT}`
    : location?.origin || "");
  const paths = localReact ? ["/api/firestore", "/share/firestore"] : ["/share/firestore", "/api/firestore"];
  return paths.map((path) => `${origin}${path}`);
};

const request = async (db: sdk.Firestore, body: GatewayRequest, expectedUserId?: string): Promise<Record<string, unknown>> => {
  const auth = getAuth(databaseOf(db).app);
  const user = auth.currentUser;
  if (!user || (expectedUserId && user.uid !== expectedUserId)) {
    throw errorWithCode("Войдите в аккаунт для доступа к данным.", "unauthenticated");
  }
  const token = await user.getIdToken();
  if (auth.currentUser?.uid !== user.uid) {
    throw errorWithCode("Аккаунт изменился во время запроса.", "unauthenticated");
  }
  const urls = requestUrls();
  let response: Response | undefined;
  for (let index = 0; index < urls.length; index += 1) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      response = await fetch(urls[index], {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal
      });
    } catch (cause) {
      // A timed-out commit may already have succeeded. Replaying it against a
      // second endpoint could apply increment/array transforms twice.
      throw Object.assign(errorWithCode("Не удалось связаться с сервером данных.", "unavailable"), { cause });
    } finally {
      clearTimeout(timeout);
    }
    if (response.status !== 404 || index === urls.length - 1) break;
  }
  if (!response) throw errorWithCode("Сервер данных недоступен.", "unavailable");
  let result: unknown;
  try {
    result = await response.json();
    if (!isRecord(result)) {
      throw new Error("Invalid data response");
    }
  } catch (cause) {
    // A truncated response is neither a missing document nor a confirmed
    // commit. Surface the uncertainty without replaying the operation.
    throw Object.assign(errorWithCode("Не удалось прочитать ответ сервера данных.", "unavailable"), { cause });
  }
  if (!response.ok || result.ok === false) {
    const message = typeof result.error === "string" ? result.error : isRecord(result.error) ? result.error.message : undefined;
    throw Object.assign(errorWithCode(typeof message === "string" ? message : "Запрос к базе данных отклонён.",
      typeof result.code === "string" ? result.code : (response.status === 429 ? "resource-exhausted" : "unknown")), {
      status: response.status,
      retryAfterSeconds: Number(result.retryAfterSeconds || response.headers.get("Retry-After")) || 0
    });
  }
  if (auth.currentUser?.uid !== user.uid) {
    throw errorWithCode("Аккаунт изменился во время запроса.", "unauthenticated");
  }
  return result;
};

const timestampString = (value: NonNullable<SdkValue["timestampValue"]>): string => {
  if (typeof value === "string") return value;
  const seconds = Number(value.seconds);
  const nanos = Number(value.nanos ?? value.nanoseconds ?? 0);
  return `${new Date(seconds * 1000).toISOString().slice(0, 19)}.${String(nanos).padStart(9, "0")}Z`;
};

// SDK mutation/query protos use protobuf JS timestamps/bytes in some builds;
// the REST API requires their protobuf JSON representation.
const restValue = (value: SdkValue): FirestoreValue => {
  if (value.timestampValue !== undefined) return { timestampValue: timestampString(value.timestampValue) };
  if (value.bytesValue !== undefined && typeof value.bytesValue !== "string") {
    return { bytesValue: sdk.Bytes.fromUint8Array(value.bytesValue).toBase64() };
  }
  if (typeof value.doubleValue === "number" && !Number.isFinite(value.doubleValue)) {
    return { doubleValue: Number.isNaN(value.doubleValue) ? "NaN" : value.doubleValue > 0 ? "Infinity" : "-Infinity" };
  }
  if (value.arrayValue !== undefined) return {
    arrayValue: { values: (value.arrayValue.values || []).map(restValue) }
  };
  if (value.mapValue !== undefined) return {
    mapValue: { fields: Object.fromEntries(Object.entries(value.mapValue.fields || {})
      .map(([key, item]) => [key, restValue(item)])) }
  };
  // Structured variants above have been converted to their REST JSON form.
  return value as FirestoreValue;
};

const timestampValue = (value: NonNullable<SdkValue["timestampValue"]>): sdk.Timestamp => {
  if (typeof value !== "string") return new sdk.Timestamp(Number(value.seconds), Number(value.nanos || 0));
  const match = /^(.*?)(?:\.(\d{1,9}))?Z$/.exec(value);
  if (!match) return sdk.Timestamp.fromDate(new Date(value));
  return new sdk.Timestamp(Date.parse(`${match[1]}Z`) / 1000, Number((match[2] || "").padEnd(9, "0")));
};

type DecodedValue = null | boolean | number | string | sdk.Timestamp | sdk.Bytes | sdk.GeoPoint
  | sdk.DocumentReference | DecodedValue[] | { [key: string]: DecodedValue };
const decodeValue = (value: SdkValue, db: sdk.Firestore): DecodedValue => {
  if ("nullValue" in value) return null;
  if (value.booleanValue !== undefined) return value.booleanValue;
  if ("integerValue" in value) return Number(value.integerValue);
  if ("doubleValue" in value) return Number(value.doubleValue);
  if (value.stringValue !== undefined) return value.stringValue;
  if (value.timestampValue !== undefined) return timestampValue(value.timestampValue);
  if (value.bytesValue !== undefined) return typeof value.bytesValue === "string"
    ? sdk.Bytes.fromBase64String(value.bytesValue) : sdk.Bytes.fromUint8Array(value.bytesValue);
  if (value.geoPointValue !== undefined) return new sdk.GeoPoint(value.geoPointValue.latitude, value.geoPointValue.longitude);
  if (value.referenceValue !== undefined) {
    const prefix = `${databaseName(db)}/`;
    if (!value.referenceValue.startsWith(prefix)) throw errorWithCode("Cross-database references are unsupported");
    return sdk.doc(db, value.referenceValue.slice(prefix.length));
  }
  if (value.arrayValue !== undefined) return (value.arrayValue.values || []).map((item) => decodeValue(item, db));
  if (value.mapValue !== undefined) return Object.fromEntries(Object.entries(value.mapValue.fields || {})
    .map(([key, item]) => [key, decodeValue(item, db)]));
  throw errorWithCode("Unsupported Firestore value returned by server", "data-loss");
};

const segments = (path: SdkPath): string[] => {
  if (typeof path.toArray === "function") return path.toArray();
  if (isUnknownArray(path.segments)) return path.segments.slice(path.offset || 0, (path.offset || 0) + path.len);
  throw errorWithCode("Unsupported Firebase SDK field path", "failed-precondition");
};

const fieldValue = (fields: Record<string, FirestoreValue>, path: SdkPath): FirestoreValue | null => {
  let value: FirestoreValue = { mapValue: { fields } };
  for (const segment of segments(path)) {
    const nested = value?.mapValue?.fields;
    if (!nested || !Object.prototype.hasOwnProperty.call(nested, segment)) return null;
    value = nested[segment];
  }
  return value;
};

function documentSnapshot<T>(reference: sdk.DocumentReference<T>, raw: FirestoreDocument, queryDocument: true): GatewayQueryDocumentSnapshot<T>;
function documentSnapshot<T>(reference: sdk.DocumentReference<T>, raw: FirestoreDocument | null, queryDocument?: false): GatewayDocumentSnapshot<T>;
function documentSnapshot<T>(reference: sdk.DocumentReference<T>, raw: FirestoreDocument | null, queryDocument = false): GatewayDocumentSnapshot<T> {
  const ref = internalReference(reference);
  const db = databaseOf(ref);
  const document = raw ? {
    key: ref._key,
    data: {
      value: { mapValue: { fields: raw.fields || {} } },
      field: (path: SdkPath) => fieldValue(raw.fields || {}, path)
    },
    isEqual(other: { data?: { value?: SdkValue } } | null) { return JSON.stringify(this.data.value) === JSON.stringify(other?.data?.value); }
  } : null;
  // Runtime SDK constructors accept their internal parsed document model.
  // The adapter regression verifies this boundary against the pinned SDK.
  const Type = (queryDocument ? sdk.QueryDocumentSnapshot : sdk.DocumentSnapshot) as unknown as
    new (database: sdk.Firestore, writer: { convertValue(value: SdkValue): DecodedValue },
      key: InternalReference<T>["_key"], parsedDocument: typeof document, metadata: sdk.SnapshotMetadata,
      converter: sdk.FirestoreDataConverter<T> | null) => GatewayDocumentSnapshot<T>;
  const snapshot = new Type(db, { convertValue: (value: SdkValue) => decodeValue(value, db) },
    ref._key, document, metadata(), ref.converter);
  snapshot._gatewayDocument = raw;
  return snapshot;
}

const querySnapshot = <T,>(reference: sdk.Query<T>, documents: FirestoreDocument[], previous: GatewayQuerySnapshot<T> | null = null): GatewayQuerySnapshot<T> => {
  const query = unwrap(reference);
  const db = databaseOf(query);
  const prefix = `${databaseName(db)}/`;
  const docs = documents.map((raw) => {
    if (!raw.name?.startsWith(prefix)) throw errorWithCode("Unexpected document path", "data-loss");
    const ref = sdk.doc(db, raw.name.slice(prefix.length)).withConverter(query.converter as sdk.FirestoreDataConverter<T>);
    return documentSnapshot(ref, raw, true);
  });
  // Indices describe the sequential transformation from the previous result,
  // including removal/reordering, as expected by Firestore docChanges users.
  const changes = () => {
    const remaining = [...(previous?.docs || [])];
    const result: sdk.DocumentChange<T>[] = [];
    const nextPaths = new Set(docs.map((item) => item.ref.path));
    for (let index = remaining.length - 1; index >= 0; index -= 1) {
      if (!nextPaths.has(remaining[index].ref.path)) {
        result.push({ type: "removed", doc: remaining[index], oldIndex: index, newIndex: -1 });
        remaining.splice(index, 1);
      }
    }
    docs.forEach((item, newIndex) => {
      const oldIndex = remaining.findIndex((old) => old.ref.path === item.ref.path);
      if (oldIndex === -1) {
        remaining.splice(newIndex, 0, item);
        result.push({ type: "added", doc: item, oldIndex: -1, newIndex });
      } else if (oldIndex !== newIndex || JSON.stringify(remaining[oldIndex]._gatewayDocument) !== JSON.stringify(item._gatewayDocument)) {
        remaining.splice(oldIndex, 1);
        remaining.splice(newIndex, 0, item);
        result.push({ type: "modified", doc: item, oldIndex, newIndex });
      }
    });
    return result;
  };
  return { docs, query, metadata: metadata(), size: docs.length, empty: docs.length === 0,
    forEach(callback: (snapshot: sdk.QueryDocumentSnapshot<T>) => void, thisArg?: unknown) { docs.forEach((item) => callback.call(thisArg, item)); },
    docChanges: changes };
};

const OPERATORS: Record<sdk.WhereFilterOp, FieldOperator> = {
  "<": "LESS_THAN", "<=": "LESS_THAN_OR_EQUAL", "==": "EQUAL", "!=": "NOT_EQUAL",
  ">=": "GREATER_THAN_OR_EQUAL", ">": "GREATER_THAN", "array-contains": "ARRAY_CONTAINS",
  in: "IN", "not-in": "NOT_IN", "array-contains-any": "ARRAY_CONTAINS_ANY"
};
const canonicalPath = (path: SdkPath): string => path.canonicalString();
const flattenFilters = (filters: SdkFilter[]): SdkFieldFilter[] => filters.flatMap((filter) => filter.filters ? flattenFilters(filter.filters) : [filter]);
const serializeFilter = (filter: SdkFilter): QueryFilter => {
  if (filter.filters) return { compositeFilter: {
    op: filter.op === "or" ? "OR" : "AND", filters: filter.filters.map(serializeFilter)
  } };
  const field = { fieldPath: canonicalPath(filter.field) };
  const value = restValue(filter.value);
  if (["==", "!="].includes(filter.op) && ("nullValue" in value ||
    ("doubleValue" in value && Number.isNaN(Number(value.doubleValue))))) {
    return { unaryFilter: { field, op: `${filter.op === "==" ? "IS" : "IS_NOT"}_${"nullValue" in value ? "NULL" : "NAN"}` } };
  }
  if (!OPERATORS[filter.op]) throw errorWithCode("Unsupported Firestore query operator");
  return { fieldFilter: { field, op: OPERATORS[filter.op], value } };
};

const serializeQuery = <T,>(reference: sdk.Query<T>) => {
  const value = internalQuery(reference)._query;
  if (!value?.path || !isUnknownArray(value.explicitOrderBy)) {
    throw errorWithCode("Unsupported Firebase SDK query representation", "failed-precondition");
  }
  const path = canonicalPath(value.path);
  const group = value.collectionGroup;
  const pathParts = path.split("/").filter(Boolean);
  const collectionId = group || pathParts.pop();
  if (!collectionId) throw errorWithCode("A Firestore collection is required");
  const parent = group ? path : pathParts.join("/");
  const order: QueryOrder[] = value.explicitOrderBy.map(({ field, dir }) => ({
    field: { fieldPath: canonicalPath(field) }, direction: dir === "desc" ? "DESCENDING" : "ASCENDING"
  }));
  if (!order.length) {
    const inequality = flattenFilters(value.filters).find((filter) => ["<", "<=", ">", ">=", "!=", "not-in"].includes(filter.op));
    if (inequality && canonicalPath(inequality.field) !== "__name__") {
      order.push({ field: { fieldPath: canonicalPath(inequality.field) }, direction: "ASCENDING" });
    }
  }
  if (!order.some((item) => item.field.fieldPath === "__name__")) {
    order.push({ field: { fieldPath: "__name__" }, direction: order[order.length - 1]?.direction || "ASCENDING" });
  }
  const reverse = value.limitType === "L";
  if (reverse && !value.explicitOrderBy.length) throw errorWithCode("limitToLast requires orderBy");
  if (reverse) order.forEach((item) => { item.direction = item.direction === "ASCENDING" ? "DESCENDING" : "ASCENDING"; });
  const start = reverse ? value.endAt : value.startAt;
  const end = reverse ? value.startAt : value.endAt;
  const filters = value.filters.map(serializeFilter);
  const structuredQuery: StructuredQuery = {
    from: [{ collectionId, ...(group ? { allDescendants: true } : {}) }],
    orderBy: order,
    ...(filters.length ? { where: filters.length === 1 ? filters[0] : { compositeFilter: { op: "AND", filters } } } : {}),
    ...(value.limit != null ? { limit: value.limit } : {}),
    ...(start ? { startAt: { values: start.position.map(restValue), before: start.inclusive } } : {}),
    ...(end ? { endAt: { values: end.position.map(restValue), before: !end.inclusive } } : {})
  };
  return { parent, structuredQuery, reverse };
};

const readDocument = async <T,>(reference: sdk.DocumentReference<T>, fresh = false): Promise<GatewayDocumentSnapshot<T>> => {
  const ref = unwrap(reference);
  const result = await request(databaseOf(ref), { operation: "get", path: ref.path, ...(fresh ? { fresh: true } : {}) });
  if (result.document !== null && !isFirestoreDocument(result.document)) throw errorWithCode("Missing document response", "data-loss");
  return documentSnapshot(ref, result.document);
};
export const getDoc = <T,>(reference: sdk.DocumentReference<T>) => readDocument(reference);
export const getDocFromServer = <T,>(reference: sdk.DocumentReference<T>) => readDocument(reference, true);

const readQuery = async <T,>(reference: sdk.Query<T>, fresh = false): Promise<GatewayQuerySnapshot<T>> => {
  const query = unwrap(reference);
  const db = databaseOf(query);
  const userId = getAuth(db.app).currentUser?.uid;
  const { parent, structuredQuery, reverse } = serializeQuery(query);
  const documents: FirestoreDocument[] = [];
  const documentPositions = new Map<string, number>();
  const seenCursors = new Set<string>();
  let cursor: GatewayCursor | undefined;
  let remaining = structuredQuery.limit ?? Infinity;
  while (remaining > 0) {
    const result = await request(db, { operation: "query", parent, structuredQuery,
      pageSize: Math.min(PAGE_SIZE, remaining), ...(cursor ? { cursor } : {}), ...(fresh ? { fresh: true } : {}) }, userId);
    if (!isUnknownArray(result.documents) || !result.documents.every(isFirestoreDocument)) throw errorWithCode("Missing query response", "data-loss");
    const page: FirestoreDocument[] = result.documents.slice(0, remaining);
    // A concurrent update of the ordering field can move a document into a
    // later page. Keep a single entry so profile statistics cannot count it
    // twice. Separate paginated requests are not a point-in-time snapshot.
    page.forEach((document) => {
      const position = documentPositions.get(document.name);
      if (position !== undefined) documents[position] = document;
      else { documentPositions.set(document.name, documents.length); documents.push(document); }
    });
    remaining -= page.length;
    if (!result.nextCursor || remaining <= 0) break;
    const cursorKey = JSON.stringify(result.nextCursor);
    if (!page.length || seenCursors.has(cursorKey)) throw errorWithCode("Query pagination did not advance", "data-loss");
    seenCursors.add(cursorKey);
    if (!isRecord(result.nextCursor) || typeof result.nextCursor.before !== "boolean"
      || !isUnknownArray(result.nextCursor.values) || !result.nextCursor.values.every(isFirestoreValue)) {
      throw errorWithCode("Invalid query cursor response", "data-loss");
    }
    cursor = { before: result.nextCursor.before, values: result.nextCursor.values };
  }
  return querySnapshot(query, reverse ? documents.reverse() : documents);
};
export const getDocs = <T,>(reference: sdk.Query<T>) => readQuery(reference);
export const getDocsFromServer = <T,>(reference: sdk.Query<T>) => readQuery(reference, true);
export const getCountFromServer = async <T,>(reference: sdk.Query<T>) => {
  const snapshot = await getDocs(reference);
  return { query: unwrap(reference), data: () => ({ count: snapshot.size }) };
};

// Use the installed SDK's local write parser rather than maintaining another
// implementation of mergeFields, converters, nested FieldPath and sentinels.
// No SDK batch is committed. The representation is covered by gateway tests
// and must be rechecked when upgrading Firebase.
const localBatch = (db: sdk.Firestore): ParsedBatch => {
  const batch = sdk.writeBatch(databaseOf(db)) as unknown as ParsedBatch;
  if (!isUnknownArray(batch._mutations)) throw errorWithCode("Unsupported Firebase SDK batch representation", "failed-precondition");
  return batch;
};

const knownTransforms = (db: sdk.Firestore): Map<Function, string> => {
  let kinds = transformKinds.get(db);
  if (!kinds) {
    const batch = localBatch(db);
    batch.set(sdk.doc(db, "__gateway_parser__/transforms"), {
      timestamp: sdk.serverTimestamp(), union: sdk.arrayUnion(1),
      remove: sdk.arrayRemove(1), increment: sdk.increment(1)
    });
    kinds = new Map(batch._mutations[0].fieldTransforms.map(({ field, transform }) =>
      [transform.constructor, canonicalPath(field)]));
    transformKinds.set(db, kinds);
  }
  return kinds;
};

const serializeMutation = (db: sdk.Firestore, mutation: SdkMutation): FirestoreWrite => {
  const name = `${databaseName(db)}/${canonicalPath(mutation.key.path)}`;
  let write: FirestoreWrite;
  if (mutation.type === 2) write = { delete: name };
  else if (mutation.type === 0 || mutation.type === 1) {
    const data = mutation.type === 0 ? mutation.value : mutation.data;
    write = { update: { name, fields: restValue(data.value).mapValue?.fields } };
    if (mutation.type === 1) write.updateMask = { fieldPaths: mutation.fieldMask.fields.map(canonicalPath) };
  } else throw errorWithCode("Unsupported Firebase mutation", "failed-precondition");
  if (mutation.fieldTransforms?.length) {
    const kinds = knownTransforms(db);
    write.updateTransforms = mutation.fieldTransforms.map(({ field, transform }): FieldTransform => {
      const fieldPath = canonicalPath(field);
      switch (kinds.get(transform.constructor)) {
        case "timestamp": return { fieldPath, setToServerValue: "REQUEST_TIME" };
        case "union":
        case "remove": {
          if (!transform.elements) throw errorWithCode("Missing array transform elements", "failed-precondition");
          const values = transform.elements.map(restValue);
          return kinds.get(transform.constructor) === "union"
            ? { fieldPath, appendMissingElements: { values } } : { fieldPath, removeAllFromArray: { values } };
        }
        case "increment": {
          if (!transform.operand) throw errorWithCode("Missing increment operand", "failed-precondition");
          return { fieldPath, increment: restValue(transform.operand) };
        }
        default: throw errorWithCode("Unsupported Firebase transform", "failed-precondition");
      }
    });
  }
  if (mutation.precondition?.exists !== undefined) write.currentDocument = { exists: mutation.precondition.exists };
  return write;
};

const notifyCommitted = (db: sdk.Firestore) => {
  listeners.forEach((listener) => {
    if (listener.db === db) listener.refresh();
  });
};

const commit = async (db: sdk.Firestore, writes: FirestoreWrite[], userId?: string): Promise<void> => {
  if (!writes.length) return;
  await request(db, { operation: "commit", writes }, userId);
  if (writes.some((write) => !write.verify)) notifyCommitted(db);
};

export const writeBatch = (firestore: sdk.Firestore): sdk.WriteBatch => {
  const db = databaseOf(firestore);
  const batch = localBatch(db);
  let committed = false;
  const assertOpen = () => {
    if (committed) throw errorWithCode("A write batch can no longer be used after commit()", "failed-precondition");
  };
  const result: sdk.WriteBatch = {
    set(ref: sdk.DocumentReference, data: unknown, options?: sdk.SetOptions) { assertOpen(); batch.set(unwrap(ref), data, options); return result; },
    update(ref: sdk.DocumentReference, ...args: unknown[]) { assertOpen(); batch.update(unwrap(ref), ...args); return result; },
    delete(ref: sdk.DocumentReference) { assertOpen(); batch.delete(unwrap(ref)); return result; },
    async commit() {
      assertOpen();
      committed = true;
      await commit(db, batch._mutations.map((mutation) => serializeMutation(db, mutation)));
    }
  };
  return result;
};

export const setDoc: typeof sdk.setDoc = async (reference: unknown, data: unknown, options?: sdk.SetOptions) => {
  const db = databaseOf(reference);
  const batch = localBatch(db);
  batch.set(unwrap(reference) as sdk.DocumentReference, data, options);
  await commit(db, batch._mutations.map((mutation) => serializeMutation(db, mutation)));
};
export const updateDoc: typeof sdk.updateDoc = async (reference: unknown, ...args: unknown[]) => {
  const db = databaseOf(reference);
  const batch = localBatch(db);
  batch.update(unwrap(reference) as sdk.DocumentReference, ...args);
  await commit(db, batch._mutations.map((mutation) => serializeMutation(db, mutation)));
};
export const deleteDoc: typeof sdk.deleteDoc = (reference: unknown) => writeBatch(databaseOf(reference)).delete(unwrap(reference) as sdk.DocumentReference).commit();
export const addDoc: typeof sdk.addDoc = async <T,>(reference: sdk.CollectionReference<T> | firebase.firestore.CollectionReference<T>, data: sdk.WithFieldValue<T>) => {
  const ref = sdk.doc(unwrap(reference) as sdk.CollectionReference<T>);
  const db = databaseOf(ref);
  const batch = localBatch(db);
  batch.set(ref, data);
  const write = serializeMutation(db, batch._mutations[0]);
  write.currentDocument = { exists: false };
  await commit(db, [write]);
  return ref;
};

export const runTransaction = async <T,>(firestore: sdk.Firestore, updateFunction: (transaction: sdk.Transaction) => Promise<T>, options: sdk.TransactionOptions = {}): Promise<T> => {
  const db = databaseOf(firestore);
  const userId = getAuth(db.app).currentUser?.uid;
  const maxAttempts = options.maxAttempts ?? 5;
  if (!Number.isInteger(maxAttempts) || maxAttempts < 1) throw errorWithCode("maxAttempts must be a positive integer");
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const batch = localBatch(db);
    const reads = new Map<string, { ref: { path: string }; raw: FirestoreDocument | null }>();
    const transaction: sdk.Transaction = {
      async get<T>(reference: sdk.DocumentReference<T>): Promise<GatewayDocumentSnapshot<T>> {
        const ref = unwrap(reference);
        if (batch._mutations.length) throw errorWithCode("Firestore transactions require all reads before writes", "failed-precondition");
        if (!reads.has(ref.path)) {
          const response = await request(db, { operation: "get", path: ref.path, fresh: true }, userId);
          if (response.document !== null && !isFirestoreDocument(response.document)) throw errorWithCode("Missing transaction document", "data-loss");
          reads.set(ref.path, { ref, raw: response.document });
        }
        return documentSnapshot(ref, reads.get(ref.path)!.raw);
      },
      set(ref: sdk.DocumentReference, data: unknown, setOptions?: sdk.SetOptions) { batch.set(unwrap(ref), data, setOptions); return transaction; },
      update(ref: sdk.DocumentReference, ...args: unknown[]) { batch.update(unwrap(ref), ...args); return transaction; },
      delete(ref: sdk.DocumentReference) { batch.delete(unwrap(ref)); return transaction; }
    };
    try {
      const value = await updateFunction(transaction);
      const unusedReads = new Map(reads);
      const writes = batch._mutations.map((mutation) => {
        const write = serializeMutation(db, mutation);
        const path = canonicalPath(mutation.key.path);
        if (unusedReads.has(path)) {
          const { raw } = unusedReads.get(path)!;
          if (!raw && write.currentDocument?.exists === true) throw errorWithCode("Cannot update a missing document", "not-found");
          if (raw && !raw.updateTime) throw errorWithCode("Transaction document has no updateTime", "data-loss");
          write.currentDocument = raw ? { updateTime: raw.updateTime } : { exists: false };
          unusedReads.delete(path);
        }
        return write;
      });
      unusedReads.forEach(({ ref, raw }) => {
        if (raw && !raw.updateTime) throw errorWithCode("Transaction document has no updateTime", "data-loss");
        writes.push({ verify: `${databaseName(db)}/${ref.path}`,
          currentDocument: raw ? { updateTime: raw.updateTime } : { exists: false } });
      });
      await commit(db, writes, userId);
      return value;
    } catch (error) {
      if (!["aborted", "failed-precondition"].includes(errorCode(error).replace(/^firestore\//, "")) || attempt === maxAttempts - 1) throw error;
      await new Promise((resolve) => setTimeout(resolve, Math.min(1000, 50 * (2 ** attempt))));
    }
  }
  throw errorWithCode("Transaction attempts exhausted", "aborted");
};

const listen = (reference: unknown, ...args: unknown[]): sdk.Unsubscribe => {
  const ref = unwrap(reference) as sdk.Query | sdk.DocumentReference;
  const db = databaseOf(ref);
  const auth = getAuth(db.app);
  if (isRecord(args[0]) && !args[0].next && !args[0].error) args.shift();
  const supplied = typeof args[0] === "function" ? { next: args[0], error: args[1], complete: args[2] } : args[0];
  const observer: Observer = isRecord(supplied) ? {
    next: typeof supplied.next === "function" ? supplied.next as Observer["next"] : undefined,
    error: typeof supplied.error === "function" ? supplied.error as Observer["error"] : undefined,
    complete: typeof supplied.complete === "function" ? supplied.complete as Observer["complete"] : undefined
  } : {};
  let closed = false;
  let running = false;
  let pending = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let previous: Snapshot | undefined;
  let previousKey: string | undefined;
  const schedule = (delay = POLL_MS) => {
    clearTimeout(timer);
    if (!closed) timer = setTimeout(refresh, delay);
  };
  const refresh = async () => {
    if (closed) return;
    if (running) { pending = true; return; }
    clearTimeout(timer);
    if (!auth.currentUser) { schedule(); return; }
    running = true;
    const userId = auth.currentUser.uid;
    let nextDelay = POLL_MS;
    try {
      let snapshot: Snapshot = ref.type === "document" ? await getDoc(ref) : await getDocs(ref);
      if (closed || auth.currentUser?.uid !== userId) return;
      const raw = "docs" in snapshot ? snapshot.docs.map((item) => item._gatewayDocument) : snapshot._gatewayDocument;
      const key = JSON.stringify(raw);
      if (key !== previousKey) {
        if (ref.type !== "document" && isUnknownArray(raw)) snapshot = querySnapshot(ref, raw, previous && "docs" in previous ? previous : null);
        previous = snapshot;
        previousKey = key;
        observer.next?.(snapshot);
      }
    } catch (error) {
      if (!closed && auth.currentUser?.uid === userId) {
        observer.error?.(error);
        nextDelay = Math.max(POLL_MS, (isRecord(error) ? Number(error.retryAfterSeconds) || 0 : 0) * 1000);
      }
    } finally {
      running = false;
      if (pending) { pending = false; schedule(0); } else schedule(nextDelay);
    }
  };
  const listener = { db, refresh };
  listeners.add(listener);
  const stopAuth = onAuthStateChanged(auth, () => {
    previous = undefined;
    previousKey = undefined;
    void refresh();
  });
  return () => {
    closed = true;
    clearTimeout(timer);
    listeners.delete(listener);
    stopAuth();
  };
};
export const onSnapshot: typeof sdk.onSnapshot = listen;

type NativeReference = sdk.DocumentReference | sdk.CollectionReference | sdk.Query;
type CompatReference = firebase.firestore.DocumentReference | firebase.firestore.CollectionReference | firebase.firestore.Query;
type CompatSnapshot = firebase.firestore.DocumentSnapshot | firebase.firestore.QuerySnapshot;
type WrapReference = (reference: NativeReference | CompatReference | null) => CompatReference | null;
const compatSnapshot = (snapshot: sdk.DocumentSnapshot | sdk.QuerySnapshot,
  originalDb: firebase.firestore.Firestore, wrapReference: WrapReference): CompatSnapshot => {
  if ("docs" in snapshot) {
    const docs = snapshot.docs.map((item) => compatSnapshot(item, originalDb, wrapReference) as firebase.firestore.QueryDocumentSnapshot);
    return { ...snapshot, _delegate: snapshot,
      query: wrapReference(snapshot.query), docs,
      forEach(callback: (document: firebase.firestore.QueryDocumentSnapshot) => void, thisArg?: unknown) {
        docs.forEach((item) => callback.call(thisArg, item));
      },
      docChanges: () => snapshot.docChanges().map((change) => ({ ...change,
        doc: compatSnapshot(change.doc, originalDb, wrapReference) }))
    } as unknown as firebase.firestore.QuerySnapshot;
  }
  return { _delegate: snapshot, id: snapshot.id, exists: snapshot.exists(), metadata: snapshot.metadata,
    ref: wrapReference(snapshot.ref), data: (options?: sdk.SnapshotOptions) => snapshot.data(options),
    get: (field: string | sdk.FieldPath, options?: sdk.SnapshotOptions) => snapshot.get(unwrap(field), options),
    isEqual: (other: firebase.firestore.DocumentSnapshot) => sdk.snapshotEqual(snapshot, unwrap(other) as unknown as sdk.DocumentSnapshot)
  } as unknown as firebase.firestore.DocumentSnapshot;
};

// Compat exposes overloads with positional arguments. Dispatch those unchanged
// to the pinned SDK parser; the public proxy keeps Firebase's original type.
const constructDocument = sdk.doc as unknown as (parent: unknown, ...path: string[]) => sdk.DocumentReference;
const constraintFactories: Record<string, (...args: unknown[]) => sdk.QueryConstraint> = {
  where: sdk.where, orderBy: sdk.orderBy, limit: sdk.limit, limitToLast: sdk.limitToLast,
  startAt: sdk.startAt, startAfter: sdk.startAfter, endAt: sdk.endAt, endBefore: sdk.endBefore
} as unknown as Record<string, (...args: unknown[]) => sdk.QueryConstraint>;
const updateParsedTransaction = (transaction: sdk.Transaction, reference: sdk.DocumentReference, args: unknown[]) => {
  const update = transaction.update as (reference: sdk.DocumentReference, ...values: unknown[]) => sdk.Transaction;
  return update.call(transaction, reference, ...args);
};

export const wrapCompatFirestore = (original: firebase.firestore.Firestore): firebase.firestore.Firestore => {
  const existing = compatDatabases.get(original);
  if (existing) return existing;
  const db = databaseOf(original);
  const references = new WeakMap<object, CompatReference>();
  const converters = new WeakMap<firebase.firestore.FirestoreDataConverter<sdk.DocumentData>, sdk.FirestoreDataConverter<sdk.DocumentData>>();
  let wrappedDb: firebase.firestore.Firestore;
  const wrapReference: WrapReference = (reference) => {
    if (!reference) return reference;
    const existingReference = references.get(reference);
    if (existingReference) return existingReference;
    const native = unwrap(reference) as NativeReference;
    const document = native.type === "document";
    const result = new Proxy(reference, {
      get(target, key): unknown {
        if (key === "_delegate") return native;
        if (key === "firestore") return wrappedDb;
        if (key === "parent") return wrapReference(document ? native.parent : (native.type === "collection" ? (native as sdk.CollectionReference).parent : null));
        if (key === "get") return async (options: firebase.firestore.GetOptions = {}) => compatSnapshot(await (document
          ? readDocument(native, options.source === "server")
          : readQuery(native, options.source === "server")), original, wrapReference);
        if (key === "onSnapshot") return (...args: unknown[]) => {
          const callbackIndex = args.findIndex((arg) => typeof arg === "function" || (isRecord(arg) && (arg.next || arg.error)));
          if (callbackIndex !== -1) {
            const callback = args[callbackIndex];
            args[callbackIndex] = typeof callback === "function"
              ? (snapshot: Snapshot) => callback(compatSnapshot(snapshot, original, wrapReference))
              : isRecord(callback) ? { ...callback, next: (snapshot: Snapshot) => {
                if (typeof callback.next === "function") callback.next(compatSnapshot(snapshot, original, wrapReference));
              } } : callback;
          }
          return listen(native, ...args);
        };
        if (key === "set" && document) return (data: sdk.DocumentData, options?: sdk.SetOptions) => options
          ? setDoc(native, data, options) : setDoc(native, data);
        if (key === "update" && document) return (...args: unknown[]) => {
          const update = updateDoc as (reference: sdk.DocumentReference, ...values: unknown[]) => Promise<void>;
          return update(native, ...args.map((argument) => unwrap(argument)));
        };
        if (key === "delete" && document) return () => deleteDoc(native);
        if (key === "add" && native.type === "collection") return async (data: sdk.DocumentData) => wrapReference(await addDoc(native as sdk.CollectionReference, data));
        if (key === "doc") return (...parts: string[]) => wrapReference(constructDocument(native, ...parts));
        if (key === "collection" && document) return (...parts: [string, ...string[]]) => wrapReference(sdk.collection(native, ...parts));
        if (typeof key === "string" && Object.prototype.hasOwnProperty.call(constraintFactories, key) && !document) {
          return (...args: unknown[]) => wrapReference(sdk.query(native, constraintFactories[key](...args.map((argument) => unwrap(argument)))));
        }
        if (key === "withConverter") return (converter: firebase.firestore.FirestoreDataConverter<sdk.DocumentData> | null) => {
          const withConverter = native.withConverter as (converter: sdk.FirestoreDataConverter<sdk.DocumentData> | null) => NativeReference;
          if (!converter) return wrapReference(withConverter.call(native, null));
          if (!converters.has(converter)) converters.set(converter, {
            toFirestore: (data: sdk.DocumentData, options?: sdk.SetOptions) => options
              ? converter.toFirestore(data, options) : converter.toFirestore(data),
            fromFirestore: (snapshot, options) => converter.fromFirestore(
              compatSnapshot(snapshot, original, wrapReference) as firebase.firestore.QueryDocumentSnapshot, options || {})
          });
          return wrapReference(withConverter.call(native, converters.get(converter)!));
        };
        if (key === "isEqual") return (other: CompatReference) => document
          ? sdk.refEqual(native, unwrap(other) as unknown as sdk.DocumentReference) : sdk.queryEqual(native, unwrap(other) as unknown as sdk.Query);
        const value: unknown = Reflect.get(target, key);
        return typeof value === "function" ? value.bind(target) : value;
      }
    }) as CompatReference;
    references.set(reference, result);
    return result;
  };
  wrappedDb = new Proxy(original, {
    get(target, key): unknown {
      if (key === "_delegate") return db;
      if (key === "doc") return (...parts: string[]) => wrapReference(constructDocument(db, ...parts));
      if (key === "collection") return (...parts: [string, ...string[]]) => wrapReference(sdk.collection(db, ...parts));
      if (key === "collectionGroup") return (id: string) => wrapReference(sdk.collectionGroup(db, id));
      if (key === "batch") return () => writeBatch(db);
      if (key === "runTransaction") return (callback: (transaction: firebase.firestore.Transaction) => Promise<unknown>, options?: sdk.TransactionOptions) => runTransaction(db, (transaction) => {
        const compatTransaction = {
          get: async (ref: firebase.firestore.DocumentReference) => compatSnapshot(await transaction.get(unwrap(ref) as unknown as sdk.DocumentReference), original, wrapReference),
          set(ref: firebase.firestore.DocumentReference, data: sdk.DocumentData, setOptions?: sdk.SetOptions) {
            const native = unwrap(ref) as unknown as sdk.DocumentReference;
            if (setOptions) transaction.set(native, data, setOptions); else transaction.set(native, data);
            return compatTransaction;
          },
          update(ref: firebase.firestore.DocumentReference, ...args: unknown[]) {
            updateParsedTransaction(transaction, unwrap(ref) as unknown as sdk.DocumentReference, args.map((argument) => unwrap(argument)));
            return compatTransaction;
          },
          delete(ref: firebase.firestore.DocumentReference) { transaction.delete(unwrap(ref) as unknown as sdk.DocumentReference); return compatTransaction; }
        };
        return callback(compatTransaction as firebase.firestore.Transaction);
      }, options);
      const value: unknown = Reflect.get(target, key);
      return typeof value === "function" ? value.bind(target) : value;
    }
  });
  compatDatabases.set(original, wrappedDb);
  compatDatabases.set(wrappedDb, wrappedDb);
  return wrappedDb;
};
